import map from 'ramda/es/map'
import reduce from 'ramda/es/reduce'
import flatten from 'ramda/es/flatten'
import sum from 'ramda/es/sum'
import pipe from 'ramda/es/pipe'
import tap from 'ramda/es/tap'
import isNil from 'ramda/es/isNil'
import AbstractPiniaModel from './AbstractPiniaModel'
import { useTestTemplateStore } from '../pinia/stores/testTemplate'
import {MainCategories,SubCategories,Question,Answer} from 'Models';


const sumQuestionDurations = reduce( (acc,question) => acc + question.duration(), 0);

//#region Test Initialisation
function initQuestion(mainCatID, subCatID)
{
    return q => {
        if(isNil(q['real_num_richtig_beantwortet']))    q['real_num_richtig_beantwortet']   = 0
        if(isNil(q['real_num_falsch_beantwortet']))     q['real_num_falsch_beantwortet']    = q['anzahl_fragen']
        if(isNil(q['scoredPoints']))                    q['scoredPoints']  = 0
        if(isNil(q['isCorrect']))                       q['isCorrect']     = false
        if(isNil(q['ignored']))                         q['ignored']       = false
        if(isNil(q['unedited']))                        q['unedited']      = false

        // Preserve backend-provided context for mixed-category inserted questions.
        if(isNil(q['mainCategoryID'])) q['mainCategoryID'] = mainCatID
        if(isNil(q['subCategoryID']))  q['subCategoryID']  = subCatID

        return q
    }
}

const getSubCatID = subCatData => subCatData.subCategoryID
const findSubCatDataFrom = subCategories => id => subCategories.find( sc => sc.id === id)


const normalizeQuestions = mainCatID => tap(subCatData => {
    subCatData.questions.forEach( initQuestion(mainCatID, subCatData.subCategoryID) )
})

const normalizeQuestionsAndGetSubCatIDs = qData =>
    qData.subCategories.map( pipe(normalizeQuestions(qData.mainCategoryID), getSubCatID) )

const createSubCategoryModelsFrom = (rawData, subCatIds) => map(pipe(findSubCatDataFrom(rawData.subCategories), SubCategories.create))(subCatIds)

const addModelsToLoadedSubCats = (qData, subCats) => SubCategories.addToLoadedSubCategories(qData.mainCategoryID, subCats)

//#endregion Test Initialisation


export class TestTemplate extends AbstractPiniaModel {
    constructor(source) { super(source) }

    static piniaStore() { return useTestTemplateStore() }

    id()            { return Number(this.source.templateID) }
    durationType()  { return this.source.templateDurationType }
    generatedTestID() { return this.source.generatedTestID }

    allTestQuestions()
    {
        return this.source.allTestQuestions
    }

    //#region Static Methods
        static initWith(rawData)
        {
            // Preserve the generatedTestID before resetting
            const currentGeneratedTestID = this.piniaStore().generatedTestID
            
            this.resetTestTemplate()

            SubCategories.resetAll()
            SubCategories.initSelectedOnes(rawData.subCategories)

            rawData.question_data.forEach(qData => {
                const subCatIds = normalizeQuestionsAndGetSubCatIDs(qData)
                const subCatModels = createSubCategoryModelsFrom(rawData, subCatIds)

                addModelsToLoadedSubCats(qData, subCatModels)
            })

            this.dispatch('setTestTemplateID', rawData.id)
            this.dispatch('setTestProfessionID', rawData.profession_id)
            this.dispatch('setTestTemplateDurationType', rawData.duration_type)
            this.dispatch('addToAllTestQuestions', rawData.question_data)
            
            // Restore the generatedTestID after reset
            if (currentGeneratedTestID) {
                this.dispatch('setGeneratedTestID', currentGeneratedTestID)
            }
        }

        static create(source) { return new TestTemplate(source) }

        static getSelected()
        {
            const store = this.piniaStore()
            return this.create({
                templateID:             store.templateID,
                templateProfessionID:   store.templateProfessionID,
                templateDurationType:   store.templateDurationType,
                generatedTestID:        store.generatedTestID,
                allTestQuestions:       store.allTestQuestions,
            })
        }

        static isLastSubCategory()              { return this.piniaStore().isLastSubCategory }
        static isLastQuestion()                 { return this.piniaStore().isLastQuestion }
        static isLastQuestionInSubCategory()    { return this.piniaStore().isLastQuestionInSubCategory }
        static isFirstQuestionInSubCategory()   { return this.piniaStore().isFirstQuestionInSubCategory }
        static isLastQuestionInBlock()          { return this.piniaStore().isLastQuestionInBlock }
        static isFirstQuestionInBlock()         { return this.piniaStore().isFirstQuestionInBlock }
        static isFirstQuestionOverall()         { return this.piniaStore().isFirstQuestionOverall }

        static allTestQuestionsAlone()          { return this.piniaStore().allTestQuestionsAlone.map(Question.create) }
        static actualTestDuration()             { return sumQuestionDurations(this.allTestQuestionsAlone()) }
        static actualTestDurationInMin()        { return Math.ceil(this.actualTestDuration() / 60) }

        static additionalDuration()
        {
            let popupsTime                  = 0

            const subCatFor                 = id     => SubCategories.findById(id)
            const exampleQuestionTime       = subCat => subCat.hasExampleQuestion() ? Number(subCat.exampleQuestion().duration()) : 0
            const testToMemorizeTime        = subCat => subCat.hasTextToMemorize() ? Number(subCat.timeToMemorize()) : 0
            const blockTime                 = block  => block.hasTextToMemorize() ? Number(block.timeToMemorize()) : 0

            const allBlocksTime             = (subCat,questions) =>
                subCat.hasBlocks(questions) ? sum(subCat.blocks(questions).map(blockTime)) : 0

            const calculateAdditionalTime   = sc => {
                popupsTime += 5
                const subCat    = subCatFor(sc.subCategoryID)
                const questions = sc.questions.map( q => Question.create(q) )
                return testToMemorizeTime(subCat) + exampleQuestionTime(subCat) + allBlocksTime(subCat, questions)
            }

            const extractSubCategories      = mc => mc.subCategories.map( calculateAdditionalTime )

            const sumTestDataTimes          = pipe(flatten, sum)
            const allTestDataTimes          = sumTestDataTimes(this.piniaStore().allTestQuestions.map( extractSubCategories ))

            return allTestDataTimes + popupsTime
        }

        static additionalDurationInMin()        { return Math.ceil(this.additionalDuration() / 60) }
        static overallTestDuration()            { return this.actualTestDuration() + this.additionalDuration() }
        static overallTestDurationInMin()       { return this.actualTestDurationInMin() + this.additionalDurationInMin() }

        static currentQuestion()                { return Question.create(this.piniaStore().currentTestQuestion); }
        static previousQuestion()               { return Question.create(this.piniaStore().previousTestQuestion); }
        static currentQuestions()               { return this.piniaStore().currentTestQuestions.map(Question.create); }
        static allQuestions()                   { return flatten(flatten(this.piniaStore().allTestQuestions.map(q => q.subCategories)).map(q => q.questions))}
        static allQuestionsCount()              { return this.allQuestions().length }
        static currentQuestionIndex()           { return Number(this.piniaStore().currentTestQuestionIndex); }
        static currentQuestionsCount()          { return Number(this.piniaStore().currentTestQuestionsCount); }
        static currentQuestionsDuration()       { return sumQuestionDurations(this.currentQuestions()) }

        static currentQuestionsSubCategories()  { return flatten(this.piniaStore()
                                                             .allTestQuestions
                                                             .map( mc => mc.subCategories.map( sc => sc.questions)))
                                                             .map(q=> SubCategories.createFromId(q.subCategoryID)); }

        static remainingQuestionsOfCurrentSubCategory() { return this.piniaStore()
                                                                     .remainingTestQuestionsOfCurrentSubCategory
                                                                     .map( q => Question.create(q) ) }

        static allRemainingQuestions()          { return this.piniaStore()
                                                             .allRemainingQuestions
                                                             .map( q => Question.create(q) )}

        static allMainCategories()              { return this.piniaStore()
                                                             .allTestQuestions
                                                             .map( mc => MainCategories.findById(mc.mainCategoryID)); }

        static subCategoriesOfMainCategory(id)  { return this.piniaStore()
                                                             .subcategoriesOfMainCategory(id)
                                                             .map( sc => SubCategories.findById(sc.subCategoryID) ); }

        static countOfAllSubCategories()         { return this.piniaStore().countOfAllSubCategories }
        static overallIndexOfCurrentSubCategory(){ return this.piniaStore().overallIndexOfCurrentSubCategory }
        static durationType()                    { return this.piniaStore().templateDurationType }
        static setGeneratedTestID(id)            { this.dispatch('setGeneratedTestID', id) }

        static ignoreCurrentQuestion()      { Answer.setToIgnoredById(this.currentQuestion().id()); }
        static goToNextQuestion()           { this.dispatch('goToNextTestQuestion'); }
        static goToPreviousQuestion()       { this.dispatch('goToPrevTestQuestion'); }

        static goToQuestionWithIndex(idx)   { this.dispatch('goToTestQuestionWithIndex', idx); }
        static goToNextSubCategory()        { this.dispatch('goToNextTestSubCategory'); }

        static markCurrentQuestion()        { this.dispatch('markTestQuestion') }
        static unmarkCurrentQuestion()      { this.dispatch('unmarkTestQuestion') }

        static resetTestTemplate()          { this.dispatch('resetTestTemplate'); }
}
